import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  type CardPaymentFormFields,
  useBreakpoint
} from '@cencosud-cencommerce/paris-uikit';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';

import { LoadingSpinner } from '@/Application/Components/LoadingSpinner';
import { useAppSelector } from '@/Application/Hooks/Redux';
import { selectBuyNowCartLoading } from '@/Application/Selectors/buyNow/useBuyNowCart';
import { BuyNowErrorModal } from '@/modules/buy-now/components/BuyNowErrorModal';
import { BuyNowLoader } from '@/modules/buy-now/components/loading';
import { useSendBuyNowEcommerceEvents } from '@/modules/shared/utils/gtmEvents/events/useSendBuyNowEcommerceEvents';
import { useSendBuyNowPageVirtualEvent } from '@/modules/shared/utils/gtmEvents/events/useSendBuyNowPageVirtualEvent';
import { useOutOfStockProduct } from '@/Pages/BuyNowPage/hooks/useOutOfStockProduct';
import ModalOutOfStock from '@/Pages/BuyNowPage/OutOfStock/ModalOutOfStock';
import BuyNowCouponComponent from '@/Pages/BuyNowPage/PaymentAndShipping/partials/Coupon/BuyNowCouponComponent';
import { DEFAULT_FORM_VALUES } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/SavedCardPayment/constants';
import { PaymentAndShipping } from '@/Pages/BuyNowPage/PaymentAndShipping/PaymentAndShipping';
import { validationSchema } from '@/Pages/BuyNowPage/PaymentAndShipping/paymentAndShipping.schema';
import { BuyNowSummary } from '@/Pages/BuyNowPage/Summary/BuyNowSummary';
import useAlternativePayments from '@/Pages/BuyNowPage/useAlternativePayments';
import useBuyNowPageInitializer from '@/Pages/BuyNowPage/useBuyNowPageInitializer';
import useGetShippingGrouping from '@/sections/shippingMethods/hooks/useGetShippingGrouping';

export const BuyNowPageContent: FC = () => {
  const { isInitializing } = useBuyNowPageInitializer();
  const { isMobile } = useBreakpoint();

  useSendBuyNowEcommerceEvents();
  useSendBuyNowPageVirtualEvent();

  const { isInitialLoading, isLoading: isLoadingGrouping } =
    useGetShippingGrouping();
  const isCartLoading = useAppSelector(selectBuyNowCartLoading);
  useAlternativePayments();

  const isShowingInitialLoader = Boolean(isInitializing || isInitialLoading);
  const isOtherOperationsLoading = Boolean(isCartLoading || isLoadingGrouping);

  const outOfStockProduct = useOutOfStockProduct();

  const formMethods = useForm<CardPaymentFormFields>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: DEFAULT_FORM_VALUES
  });

  if (outOfStockProduct) {
    return <ModalOutOfStock product={outOfStockProduct} />;
  }

  if (isShowingInitialLoader) {
    return <BuyNowLoader />;
  }

  return (
    <main
      className={classNames(
        'co-max-w-[1536px] co-w-full co-grid co-gap-5 co-grid-cols-1 tablet_w:co-grid-cols-[1fr_375px] desktop:co-grid-cols-[1fr_430px]'
      )}
    >
      <LoadingSpinner showSpinner={isOtherOperationsLoading} />

      <FormProvider {...formMethods}>
        <PaymentAndShipping />
        <aside className="co-flex co-flex-col co-gap-[16px] co-flex-1">
          <BuyNowSummary />
          {!isMobile && <BuyNowCouponComponent />}
        </aside>
      </FormProvider>

      <BuyNowErrorModal />
      {/* Modal container for Buy Now error modal */}
      <div id="buy-now-payment-error-modal"></div>
    </main>
  );
};
